import React, { useState } from 'react';
import { Form } from 'react-bootstrap';
import { AgencyState, InputFieldState } from '../types';
import IdentificationNumberForm from '../IdentificationNumberForm';
import PhoneNumberForm from '../PhoneNumberForm';
import AddressForm from '../AddressForm';
import NameForm from '../NameForm';
import RepresentativeForm from '../RepresentativeForm';

/**
 * Agencyコンポーネント用のPropsです。
 * @interface AgencyProps
 */
interface Props {

  /**
   * 開閉フラグ
   * @type {boolean}
   * @memberof AgencyProps
   */
  isAgencyOpened: boolean;

  /**
   * 代理人
   * @type {string}
   * @memberof AgencyProps
   */
  agency: AgencyState;

  /**
   * 代理人を設定する関数
   * @memberof ApplicantProps
   */
  onChange: (updatedAgency: AgencyState) => void;

}

/**
 * 代理人フォームのコンポーネントです。
 * @param {function} props.onChange - 代理人が変更されたときに呼ばれるコールバック関数
 * @returns {JSX.Element} - 代理人フォームのJSX要素
 */
const AgencyForm: React.FC<Props> = ({ isAgencyOpened, agency, onChange }) => {

  const [isRequired, setIsRequired] = useState(false);

  /**
   * 識別番号を更新する関数です。
   * @param {string} identificationNumber - 識別番号
   * @returns {void}
   */
  const updateIdentificationNumber = (identificationNumber: InputFieldState): void => {

    if(identificationNumber.value !== '' || agency.address.value !== '' || agency.name.value !== '' || agency.representative.value !== '' || agency.phoneNumber.value !== '') {
      setIsRequired(true);
    } else {
      setIsRequired(false);
    }

    onChange(
      {
        identificationNumber: identificationNumber,
        address: agency.address,
        name: agency.name,
        representative: agency.representative,
        phoneNumber: agency.phoneNumber
      }
    );
  };

  /**
   * 住所又は居所を更新する関数です。
   * @param {InputFieldState} address - 住所又は居所
   * @returns {void}
   */
  const updateAddress = (address: InputFieldState): void => {

    if(agency.identificationNumber.value !== '' || address.value !== '' || agency.name.value !== '' || agency.representative.value !== '' || agency.phoneNumber.value !== '') {
      setIsRequired(true);
    } else {
      setIsRequired(false);
    }

    onChange(
      {
        identificationNumber: agency.identificationNumber,
        address: address,
        name: agency.name,
        representative: agency.representative,
        phoneNumber: agency.phoneNumber
      }
    );

  };

  /**
   * 氏名又は名称を更新する関数です。
   * @param {InputFieldState} name - 氏名又は名称
   * @returns {void}
   */
  const updateName = (name: InputFieldState): void => {

    if(agency.identificationNumber.value !== '' || agency.address.value !== '' || name.value !== '' || agency.representative.value !== '' || agency.phoneNumber.value !== '') {
      setIsRequired(true);
    } else {
      setIsRequired(false);
    }

    onChange(
      {
        identificationNumber: agency.identificationNumber,
        address: agency.address,
        name: name,
        representative: agency.representative,
        phoneNumber: agency.phoneNumber
      }
    );

  };

  /**
   * 代表者を更新する関数です。
   * @param {InputFieldState} representative - 代表者
   * @returns {void}
   */
  const updateRepresentative = (representative: InputFieldState): void => {

    if(agency.identificationNumber.value !== '' || agency.address.value !== '' || agency.name.value !== '' || representative.value !== '' || agency.phoneNumber.value !== '') {
      setIsRequired(true);
    } else {
      setIsRequired(false);
    }

    onChange(
      {
        identificationNumber: agency.identificationNumber,
        address: agency.address,
        name: agency.name,
        representative: representative,
        phoneNumber: agency.phoneNumber
      }
    );
  };

  /**
   * 電話番号を更新する関数です。
   * @param {InputFieldState} phoneNumber - 電話番号
   * @returns {void}
   */
  const updatephoneNumber = (phoneNumber: InputFieldState): void => {

    if(agency.identificationNumber.value !== '' || agency.address.value !== '' || agency.name.value !== '' || agency.representative.value !== '' || phoneNumber.value !== '') {
      setIsRequired(true);
    } else {
      setIsRequired(false);
    }

    onChange(
      {
        identificationNumber: agency.identificationNumber,
        address: agency.address,
        name: agency.name,
        representative: agency.representative,
        phoneNumber: phoneNumber
      }
    );
  };

  return (
    <>
      <details open={isAgencyOpened}>
        <summary>代理人手続の場合はこちらから入力</summary>
        <Form.Label>代理人</Form.Label>
        <div className="ms-lg-5">
          <IdentificationNumberForm
            identificationNumber={agency.identificationNumber}
            isAgency={true}
            isRequired={isRequired}
            onChange={updateIdentificationNumber}
          />
          <AddressForm
            address={agency.address}
            isAgency={true}
            isRequired={isRequired}
            onChange={updateAddress}
          />
          <NameForm
            name={agency.name}
            isAgency={true}
            isRequired={isRequired}
            onChange={updateName}
          />
          <RepresentativeForm
            representative={agency.representative}
            name={agency.name}
            isProcedureExecutor={true}
            onChange={updateRepresentative}
          />
          <PhoneNumberForm
            phoneNumber={agency.phoneNumber}
            onChange={updatephoneNumber}
          />
        </div>
      </details>
    </>
  );
}

export default AgencyForm;